import { useState } from "react";
import { IconButton, Dialog, DialogTitle, DialogContent } from "@mui/material";
import dayjs from "dayjs";
import { FileText, Calendar, X } from "lucide-react";
import { useGetSecurityDetails } from "@/api/securities/useGetSecurityDetails";
import { Button } from "@/components/Button/Button";
import { Loader } from "@/components/loader";
import { dateFormatter } from "@/utils/date";

const MARKET_COMMENT_SECURITY_ID = "844972" as const;

export const MarketComment = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, loading, error } = useGetSecurityDetails(
    MARKET_COMMENT_SECURITY_ID
  );

  const commentData =
    data?.security?.figuresAsObject?.latestValues?.cqcsRepComment;
  const comment = commentData?.value;
  const commentDate = commentData?.date;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setDialogOpen(true)}
        loading={loading}
        icon={<FileText className="size-3 mr-1.5" />}
      >
        Market Comment
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "85vh",
          },
        }}
      >
        <DialogTitle className="flex items-center justify-between border-b pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FileText className="size-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Market Comment</h2>
              {commentDate && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                  <Calendar className="size-3" />
                  <span>{dayjs(commentDate).format("MMMM D, YYYY")}</span>
                </div>
              )}
            </div>
          </div>
          <Button size="sm" variant="icon" onClick={() => setDialogOpen(false)}>
            <X className="size-4" />
          </Button>
        </DialogTitle>

        <DialogContent className="pt-6 pb-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          ) : error || !comment ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="size-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">
                {error
                  ? "Failed to load market comment"
                  : "No market comment available"}
              </p>
            </div>
          ) : (
            <div className="p-4 bg-muted/30 rounded-lg">
              <div
                className="prose prose-xs dark:prose-invert max-w-none text-sm
                  prose-headings:font-semibold prose-headings:text-foreground prose-headings:mt-4 prose-headings:mb-2
                  prose-h1:text-base prose-h2:text-sm prose-h3:text-sm
                  prose-p:leading-relaxed prose-p:text-foreground/75 prose-p:mb-2 prose-p:text-xs
                  prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-foreground prose-strong:font-semibold
                  prose-ul:my-2 prose-ol:my-2 prose-li:text-foreground/75 prose-li:my-0.5 prose-li:text-xs
                  prose-blockquote:border-l-primary prose-blockquote:bg-muted/50
                  prose-blockquote:py-1.5 prose-blockquote:px-3 prose-blockquote:rounded-r prose-blockquote:not-italic prose-blockquote:text-xs
                  prose-code:text-primary prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs"
                dangerouslySetInnerHTML={{ __html: comment }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
